<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import fixWebmDuration from "fix-webm-duration";

import AppButton from "@shared/ui/components/AppButton.vue";
import AppModal from "@shared/ui/components/AppModal.vue";

import { useFileUpload } from "@features/file-transfer/useFileUpload";

import type { FileAttachment } from "shared";

// Запись видеосообщения: камера → MediaRecorder → превью записи → заливка тем же
// чанковым транспортом, что и обычные файлы (постер/габариты генерит uploadFile).

const props = defineProps<{ chatId: string }>();
const emit = defineEmits<{
    send: [attachment: FileAttachment];
    close: [];
}>();

const { uploadFile } = useFileUpload();

type Stage = "starting" | "recording" | "preview" | "sending" | "error";
const stage = ref<Stage>("starting");
const errorText = ref("");

const liveRef = ref<HTMLVideoElement | null>(null);

let stream: MediaStream | null = null;
let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let mimeType = "";

const recordedUrl = ref<string | null>(null);
let recordedBlob: Blob | null = null;

// Таймер записи (отображение) + точный замер длительности для патча webm.
const seconds = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;
let recordStart = 0;

const timeLabel = computed(() => {
    const m = Math.floor(seconds.value / 60);
    const s = seconds.value % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
});

// Таймер записи живёт в заголовке модалки.
const modalTitle = computed(() =>
    stage.value === "recording"
        ? `Видеосообщение · ${timeLabel.value}`
        : "Видеосообщение"
);

// Прогресс заливки.
const progress = ref(0);
let controller: AbortController | null = null;

function pickMime(): string {
    const candidates = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm",
        "video/mp4",
    ];
    return candidates.find((c) => MediaRecorder.isTypeSupported(c)) ?? "";
}

// Квадратная запись (как видеосообщения в TG): кадры камеры центр-кропом рисуются
// в квадратный canvas, пишется его поток + аудиодорожка микрофона.
const SQUARE = 640;
let canvas: HTMLCanvasElement | null = null;
let drawRaf = 0;

function startSquareDraw(video: HTMLVideoElement): MediaStream {
    canvas = document.createElement("canvas");
    canvas.width = SQUARE;
    canvas.height = SQUARE;
    const ctx = canvas.getContext("2d")!;
    // Зеркалим запись — как живое превью (в TG видеосообщение зеркально и при
    // записи, и у получателя).
    ctx.setTransform(-1, 0, 0, 1, SQUARE, 0);
    const draw = () => {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (vw && vh) {
            const side = Math.min(vw, vh);
            ctx.drawImage(
                video,
                (vw - side) / 2,
                (vh - side) / 2,
                side,
                side,
                0,
                0,
                SQUARE,
                SQUARE
            );
        }
        drawRaf = requestAnimationFrame(draw);
    };
    draw();
    return canvas.captureStream(30);
}

function stopSquareDraw(): void {
    if (drawRaf) {
        cancelAnimationFrame(drawRaf);
        drawRaf = 0;
    }
    canvas = null;
}

async function start(): Promise<void> {
    stage.value = "starting";
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: true,
        });
    } catch (e) {
        stage.value = "error";
        // Имя исключения — на экран: NotAllowedError = запрет разрешений
        // (TCC/webview), TypeError = нет mediaDevices (небезопасный контекст),
        // NotFoundError = нет устройства.
        const detail =
            e instanceof Error ? `${e.name}: ${e.message}` : String(e);
        errorText.value = `Камера или микрофон недоступны (${detail})`;
        return;
    }
    if (liveRef.value) {
        liveRef.value.srcObject = stream;
    }
    mimeType = pickMime();
    chunks = [];
    // Записываем квадратный canvas-поток + звук микрофона.
    const recordStream = startSquareDraw(liveRef.value!);
    for (const track of stream.getAudioTracks()) {
        recordStream.addTrack(track);
    }
    recorder = new MediaRecorder(recordStream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: 2_500_000,
    });
    recorder.ondataavailable = (e) => {
        if (e.data.size) {
            chunks.push(e.data);
        }
    };
    recorder.onstop = async () => {
        let blob = new Blob(chunks, { type: mimeType || "video/webm" });
        // Chromium-баг: у webm из MediaRecorder нет метаданных длительности
        // (Infinity, кривой сик). Патчим Duration в EBML-заголовок замеренным
        // временем записи; при провале остаётся исходный blob.
        if (blob.type.includes("webm")) {
            try {
                blob = await fixWebmDuration(
                    blob,
                    performance.now() - recordStart,
                    { logger: false }
                );
            } catch {
                // Оставляем как есть — просто без метаданных длительности.
            }
        }
        recordedBlob = blob;
        recordedUrl.value = URL.createObjectURL(blob);
        stopStream();
        stage.value = "preview";
    };
    recorder.start();
    recordStart = performance.now();
    seconds.value = 0;
    timer = setInterval(() => seconds.value++, 1000);
    stage.value = "recording";
}

function stopTimer(): void {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
}

function stopStream(): void {
    stopSquareDraw();
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
}

function stopRecording(): void {
    stopTimer();
    if (recorder && recorder.state !== "inactive") {
        // Превью соберётся в recorder.onstop.
        recorder.stop();
    }
}

function discardRecording(): void {
    if (recordedUrl.value) {
        URL.revokeObjectURL(recordedUrl.value);
        recordedUrl.value = null;
    }
    recordedBlob = null;
}

// Клик по превью — пауза/продолжить (контролов у превью нет).
function togglePreview(e: Event): void {
    const video = e.target as HTMLVideoElement;
    if (video.paused) {
        void video.play();
    } else {
        video.pause();
    }
}

// Прогресс воспроизведения превью — тянет рамку по периметру квадрата.
const playbackProgress = ref(0);

function onPreviewTime(e: Event): void {
    const video = e.target as HTMLVideoElement;
    playbackProgress.value =
        Number.isFinite(video.duration) && video.duration > 0
            ? video.currentTime / video.duration
            : 0;
}

async function rerecord(): Promise<void> {
    discardRecording();
    await start();
}

async function send(): Promise<void> {
    if (!recordedBlob || stage.value === "sending") {
        return;
    }
    stage.value = "sending";
    progress.value = 0;
    controller = new AbortController();
    const ext = (mimeType || "video/webm").includes("mp4") ? "mp4" : "webm";
    const file = new File(
        [recordedBlob],
        `video-message-${Date.now()}.${ext}`,
        { type: mimeType || "video/webm" }
    );
    try {
        const attachment = await uploadFile(
            file,
            props.chatId,
            (fraction) => {
                progress.value = fraction;
            },
            controller.signal
        );
        // Помечаем как видеосообщение — лента рендерит его иначе, чем файл.
        attachment.kind = "video-note";
        emit("send", attachment);
        cleanup();
        emit("close");
    } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") {
            return;
        }
        stage.value = "error";
        // Причина — на экран: в релизной сборке консоли нет.
        const detail = e instanceof Error ? e.message : String(e);
        errorText.value = `Не удалось загрузить видео: ${detail}`;
    }
}

function cleanup(): void {
    stopTimer();
    // Отмена активной заливки подчистит недокачанный файл на сервере.
    controller?.abort();
    controller = null;
    if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;
        recorder.stop();
    }
    recorder = null;
    stopStream();
    discardRecording();
}

function cancel(): void {
    cleanup();
    emit("close");
}

onMounted(start);
onBeforeUnmount(cleanup);
</script>

<template>
    <AppModal
        :is-visible="true"
        :title="modalTitle"
        :max-width="420"
        no-padding
        @close="cancel"
    >
        <div class="mc-vrec">
            <div class="mc-vrec__screen">
                <!-- Живая камера, зеркально — записывается тоже зеркально
                     (canvas), так что превью и итог совпадают, как в TG. -->
                <video
                    v-show="stage === 'starting' || stage === 'recording'"
                    ref="liveRef"
                    class="mc-vrec__video mc-vrec__video_mirror"
                    autoplay
                    muted
                    playsinline
                />
                <!-- Превью записи: без контролов, крутится по кругу,
                     клик — пауза/продолжить. -->
                <video
                    v-if="recordedUrl && stage !== 'recording'"
                    class="mc-vrec__video"
                    :src="recordedUrl"
                    autoplay
                    loop
                    playsinline
                    @click="togglePreview"
                    @timeupdate="onPreviewTime"
                />
                <div v-if="stage === 'error'" class="mc-vrec__error">
                    {{ errorText }}
                </div>

                <!-- Действия — прямо на видео, снизу по центру. -->
                <div
                    v-if="stage === 'recording'"
                    class="mc-vrec__overlay-actions"
                >
                    <AppButton variant="pill" @click="stopRecording">
                        Готово
                    </AppButton>
                </div>

                <div
                    v-if="stage === 'preview' || stage === 'sending'"
                    class="mc-vrec__overlay-actions"
                >
                    <AppButton
                        variant="pill-ghost"
                        :disabled="stage === 'sending'"
                        @click="rerecord"
                    >
                        Перезаписать
                    </AppButton>
                    <AppButton
                        variant="pill"
                        :loading="stage === 'sending'"
                        @click="send"
                    >
                        Отправить
                    </AppButton>
                </div>

                <!-- Прогресс воспроизведения превью: бордер тянется по периметру
                     квадрата (pathLength=100 → dashoffset прямо в процентах). -->
                <svg
                    v-if="stage === 'preview' || stage === 'sending'"
                    class="mc-vrec__ring"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                >
                    <rect
                        x="0"
                        y="0"
                        width="100"
                        height="100"
                        pathLength="100"
                        :stroke-dashoffset="
                            100 - Math.min(1, playbackProgress) * 100
                        "
                    />
                </svg>
            </div>

            <!-- Все действия лежат на самом видео; отмена — закрытие модалки
                 (Esc / ×). Ряд под видео остаётся только для ошибки. -->
            <div v-if="stage === 'error'" class="mc-vrec__actions">
                <AppButton variant="ghost" @click="cancel">Закрыть</AppButton>
            </div>
        </div>
    </AppModal>
</template>

<style scoped lang="scss">
.mc-vrec {
    display: flex;
    flex-direction: column;
    gap: 14px;

    &__screen {
        position: relative;
        // Квадрат — как записываемое видео (canvas-кроп 1:1).
        aspect-ratio: 1 / 1;
        background: #000;
        // Контент модалки вплотную к краям (no-padding): боковые рамки
        // дублировали бы рамку модалки, верхняя — линию под заголовком.
        border-bottom: 1px solid var(--mc-line);
        overflow: hidden;
    }

    &__video {
        width: 100%;
        height: 100%;
        object-fit: cover;

        &_mirror {
            transform: scaleX(-1);
        }
    }

    &__error {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--mc-danger);
        font-family: var(--mc-mono);
        font-size: 12px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    // Кнопки поверх видео — снизу по центру.
    &__overlay-actions {
        position: absolute;
        bottom: 18px;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        gap: 8px;
    }

    // Прогресс воспроизведения — рамка по периметру квадрата. Линейный
    // transition чуть длиннее интервала timeupdate (~250мс) сглаживает шаги.
    &__ring {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;

        rect {
            fill: none;
            stroke: var(--mc-acid);
            // В юнитах viewBox (1 юнит ≈ 3.8px при 380px квадрате):
            // non-scaling-stroke нельзя — он ломает pathLength/dasharray
            // (штрихи пересчитываются в экранные px → рамка распадается).
            // Рисуется по кромке: внешняя половина клипается, видимо ~1px.
            stroke-width: 2;
            stroke-dasharray: 100;
            filter: drop-shadow(0 0 4px var(--mc-acid-glow));
            transition: stroke-dashoffset 0.25s linear;
        }
    }

    // Ряд под видео (ошибка): свои отступы — модалка теперь без паддингов.
    &__actions {
        display: flex;
        gap: 8px;
        padding: 0 16px 16px;
    }
}
</style>
